import { css, SerializedStyles } from '@emotion/react';
import type { ReactNode } from 'react';

import { ExpandCaret } from '@/Client/Icons';
import {
  KAI_SHIKI_TYPOGRAPHY,
  styleguideColors as COLORS,
  styleguideFontSize as FONT_SIZE,
  styleguideFontWeight as FONT_WEIGHT,
} from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideElevation as ELEVATION, styleguideFontFamily as FONT, styleguideRadius as RADIUS } from '@kaiizen/kai-shiki/core';
import { CheckIcon } from '@kaiizen/kai-shiki/react';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { ExpandIcon } from '../ExpandIcon';
import { LabeledInputSize } from './LabeledInputCommonTypes';

/**
 * Presentation kept separate from LabeledSelect's own logic, the same way CountInput's
 * `styles` prop does (see `CountInput/countInputStyles.ts`) — a re-skin builds its own
 * styles object (spreading `DEFAULT_LABELED_SELECT_STYLES` and overriding only what
 * differs) and passes it in, rather than changing the legacy default for every consumer
 * app-wide.
 */
export type LabeledSelectStyles = {
  /** Whether LabeledInputPresenter draws its floating, border-overlapping label. Re-skins
   *  that put the label outside the field entirely (see FocusedSimpleContractorPicker) turn
   *  this off and render their own label span above the field instead. */
  showFloatingLabel: boolean;
  /** Per-size type treatment for the picklist's displayed value and its options. */
  valueTypographySizes: Record<LabeledInputSize, SerializedStyles>;
  pickListDropdown: SerializedStyles;
  pickListDropdownTiny: SerializedStyles;
  pickListOption: SerializedStyles;
  pickListOptionSelected: SerializedStyles;
  /** Named apart from the `emptyMessage` prop (that one's the text; this is its style). */
  pickListEmptyMessage: SerializedStyles;
  /** Per-size box sizing shared by the closed-state value and the open-state search input. */
  inputSizes: Record<LabeledInputSize, SerializedStyles>;
  pickListText: SerializedStyles;
  pickListTextPlaceholder: SerializedStyles;
  expandIconContainer: SerializedStyles;
  pickListInlineInput: SerializedStyles;
  pickListInlineInputDisabled: SerializedStyles;
  renderExpandIcon: (expanded: boolean) => ReactNode;
  /** Rendered before each dropdown option's label — e.g. a check mark on the selected
   *  option. Always occupies the same slot so every option's label lines up whether or
   *  not it carries the mark. Returns `null` for no marker at all (the legacy look). */
  renderOptionMarker?: (isSelected: boolean) => ReactNode;
  /** Overrides the placeholder's muted color when a caller flags the field itself (e.g. a
   *  required selection that's still missing) via `frameColor` — same signal that colors
   *  the surrounding frame in LabeledInputPresenter. */
  renderFrameColorText: (frameColor: string) => SerializedStyles;
};

export const DEFAULT_LABELED_SELECT_STYLES: LabeledSelectStyles = {
  showFloatingLabel: true,
  valueTypographySizes: {
    tiny: css`
      label: VALUE_TYPOGRAPHY_SIZES_TINY;
      ${TYPOGRAPHY2.xSmall}
      font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
    `,
    small: css`
      label: VALUE_TYPOGRAPHY_SIZES_SMALL;
      ${TYPOGRAPHY2.xSmall}
      font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
    `,
    tightSmall: css`
      label: VALUE_TYPOGRAPHY_SIZES_SMALL;
      ${TYPOGRAPHY2.xSmall}
      font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
    `,
    medium: css`
      label: VALUE_TYPOGRAPHY_SIZES_MEDIUM;
      ${TYPOGRAPHY2.small}
      font-size: ${pxToRem(TYPOGRAPHY2.fontSize.small)} !important;
    `,
    tightMedium: css`
      label: VALUE_TYPOGRAPHY_SIZES_MEDIUM;
      ${TYPOGRAPHY2.small}
      font-size: ${pxToRem(TYPOGRAPHY2.fontSize.small)} !important;
    `,
    large: css`
      label: VALUE_TYPOGRAPHY_SIZES_LARGE;
      ${TYPOGRAPHY2.medium}
      font-size: ${pxToRem(TYPOGRAPHY2.fontSize.medium)} !important;
    `,
  },
  pickListDropdown: css`
    label: PICKLIST_DROPDOWN_STYLE;
    position: fixed;
    background: var(--background);
    border: 1px solid var(--border2);
    max-height: 200px;
    overflow-y: auto;
    z-index: 99999;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  `,
  pickListDropdownTiny: css`
    label: PICKLIST_DROPDOWN_STYLE_TINY;
    border-top: 1px solid var(--border2);
    top: calc(100% + 1px);
  `,
  pickListOption: css`
    label: PICKLIST_OPTION_STYLE;
    padding: ${LAYOUT2.spacing1}px ${LAYOUT2.spacing2}px;
    cursor: pointer;
    border-bottom: 1px solid var(--border2);
    ${TYPOGRAPHY2.xSmall}
    color: var(--text);

    &:hover {
      background: var(--background2);
    }

    &:last-child {
      border-bottom: none;
    }
  `,
  pickListOptionSelected: css`
    label: PICKLIST_OPTION_SELECTED_STYLE;
    background: var(--background3);
    color: var(--text);
  `,
  pickListEmptyMessage: css`
    label: PICKLIST_EMPTY_MESSAGE_STYLE;
    color: var(--text2);
    font-style: italic;
  `,
  inputSizes: {
    tiny: css``,
    small: css`
      height: 32px;
      min-height: 32px;
      max-height: 32px;
    `,
    tightSmall: css`
      height: 32px;
      min-height: 32px;
      max-height: 32px;
    `,
    medium: css`
      height: 44px;
      min-height: 44px;
      max-height: 44px;
    `,
    tightMedium: css`
      height: 44px;
      min-height: 44px;
      max-height: 44px;
    `,
    large: css`
      height: 50px;
      min-height: 50px;
      max-height: 50px;
    `,
  },
  pickListText: css`
    label: PICKLIST_TEXT_STYLE;
    cursor: pointer;
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    color: var(--text);
    box-sizing: border-box;
  `,
  pickListTextPlaceholder: css`
    label: PICKLIST_TEXT_PLACEHOLDER_STYLE;
    color: var(--text2);
    font-style: italic;
  `,
  expandIconContainer: css`
    label: EXPAND_ICON_CONTAINER_STYLE;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  `,
  pickListInlineInput: css`
    label: PICKLIST_INLINE_INPUT_STYLE;
    flex: 1;
    min-width: 0;
    width: 100%;
    border: 0;
    padding: 0;
    background: transparent;
    color: var(--text);
    outline: none;
    box-sizing: border-box;
  `,
  pickListInlineInputDisabled: css`
    label: PICKLIST_INLINE_INPUT_STYLE_DISABLED;
    font-style: italic;
    cursor: default;
  `,
  renderExpandIcon: expanded => <ExpandIcon expanded={expanded} upDownStyle color={'var(--text)'} />,
  renderFrameColorText: frameColor =>
    css`
      label: PICKLIST_TEXT_FRAME_COLOR_STYLE;
      color: ${frameColor};
      font-style: italic;
    `,
};

/** Reserves the same slot for every option — checked or not — so labels line up either
 *  way (prototype `.pgd-filter-tick`, always 11px wide, visible only on the selected item). */
const OPTION_MARKER_SLOT_STYLE = css`
  label: OPTION_MARKER_SLOT_STYLE;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  flex-shrink: 0;
`;

/**
 * The re-skin matching the ShopEdit prototype's `.sec-hdr-dd` dropdown (a plain, sharp-cornered,
 * ink-bordered field — see `no_commit/Kai_ShopEdit_Prototype.html`) rather than LabeledSelect's
 * legacy rounded/`--background`-themed look. Spreads `DEFAULT_LABELED_SELECT_STYLES` and
 * overrides only what the prototype draws differently; kept local to this one call site
 * (FocusedSimpleContractorPicker) rather than changing the default for every consumer app-wide,
 * the same way FOCUSED_COUNT_INPUT_STYLES re-skins CountInput.
 *
 * The floating label is off (see `showFloatingLabel`) — the prototype's field carries no label
 * of its own; the caller renders "Contractor" as a plain span above the field instead.
 */
export const LABELED_SELECT_STYLES: LabeledSelectStyles = {
  ...DEFAULT_LABELED_SELECT_STYLES,
  inputSizes: {
    tiny: css``,
    small: css`
      height: 32px;
    `,
    tightSmall: css`
      height: 32px;
    `,
    medium: css`
      height: 38px;
    `,
    tightMedium: css`
      height: 44px;
    `,
    large: css`
      height: 50px;
    `,
  },
  showFloatingLabel: false,
  pickListDropdown: css`
    label: PICKLIST_DROPDOWN_STYLE_PROTOTYPE;
    position: fixed;
    background: ${COLORS.white};
    border: 1px solid ${COLORS.ink};
    border-radius: ${RADIUS.sharp}px;
    max-height: 260px;
    overflow-y: auto;
    z-index: 99999;
    box-shadow: ${ELEVATION.popover};
    margin-top: 3px;
  `,
  pickListOption: css`
    label: PICKLIST_OPTION_STYLE_PROTOTYPE;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    cursor: pointer;
    border: 0;
    background: transparent;
    ${KAI_SHIKI_TYPOGRAPHY.medium}
    color: ${COLORS.ink};
    transition: background 0.1s;

    &:hover {
      background: ${COLORS.accent};
    }
  `,
  pickListOptionSelected: css`
    label: PICKLIST_OPTION_SELECTED_STYLE_PROTOTYPE;
    background: #FDFDD6;

    &:hover {
      background: ${COLORS.accent};
    }
  `,
  renderExpandIcon: expanded => <ExpandCaret expanded={expanded} color={COLORS.midtone} initialRotation={90} finalRotation={180} />,
  renderOptionMarker: isSelected => (
    <span css={OPTION_MARKER_SLOT_STYLE}>
      {isSelected ? <CheckIcon size={12} color={COLORS.ink} /> : null}
    </span>
  ),
  valueTypographySizes: {
    tiny: css`
      label: VALUE_TYPOGRAPHY_SIZES_TINY;
      font-family: ${FONT.body};
      font-size: ${pxToRem(FONT_SIZE.monoMicro)};
      font-weight: ${FONT_WEIGHT.regular};
    `,
    small: css`
      label: VALUE_TYPOGRAPHY_SIZES_SMALL;
      ${TYPOGRAPHY2.xSmall}
      font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
    `,
    tightSmall: css`
      label: VALUE_TYPOGRAPHY_SIZES_SMALL;
      ${TYPOGRAPHY2.xSmall}
      font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
    `,
    medium: css`
      label: VALUE_TYPOGRAPHY_SIZES_MEDIUM;
      ${KAI_SHIKI_TYPOGRAPHY.medium}
    `,
    tightMedium: css`
      label: VALUE_TYPOGRAPHY_SIZES_MEDIUM;
      ${TYPOGRAPHY2.small}
      font-size: ${pxToRem(TYPOGRAPHY2.fontSize.small)} !important;
    `,
    large: css`
      label: VALUE_TYPOGRAPHY_SIZES_LARGE;
      ${TYPOGRAPHY2.medium}
      font-size: ${pxToRem(TYPOGRAPHY2.fontSize.medium)} !important;
    `,
  },
};
